import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Clock, Ticket } from 'lucide-react';

const BookingSection = () => {
  const widgetRef = useRef(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (widgetRef.current && !scriptLoaded.current) {
      scriptLoaded.current = true;
      const script = document.createElement('script');
      script.src = 'https://www.vegas2hollywood.com/embed/booking-widget.js';
      script.setAttribute('data-api', 'https://www.vegas2hollywood.com/api');
      script.setAttribute('data-brand', 'v2h');
      script.setAttribute('data-container', 'v2h-booking');
      script.async = true;
      widgetRef.current.parentNode.insertBefore(script, widgetRef.current.nextSibling);
    }
  }, []);

  return (
    <section id="booking" className="section-padding bg-[#0A0A0A]" data-testid="booking-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FFD600] mb-2">Secure Your Seats Today</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white">
            Book Your Hollywood Adventure
          </h2>
          <p className="font-outfit text-base sm:text-lg text-white/70 max-w-2xl mx-auto mt-4">
            Turn your Vegas trip into a Hollywood adventure in just one day. Travel VIP-style to Los Angeles
            with stops at Santa Monica Beach, Beverly Hills, Rodeo Drive, and the Hollywood Walk of Fame.
            Enjoy a luxury ride, complimentary breakfast and lunch, and an unforgettable up-close view of the Hollywood Sign.
          </p>
          <p className="font-bebas text-xl sm:text-2xl tracking-tight text-[#00E5FF] mt-3">
            One epic day. Two iconic cities. Book now.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl border-2 border-black shadow-[6px_6px_0px_0px_rgba(255,0,122,1)] p-6 sm:p-8 mb-8"
        >
          <div className="flex flex-wrap items-center justify-center gap-6 mb-6 pb-6 border-b-2 border-gray-100">
            <div className="flex items-center gap-2 font-outfit text-sm font-medium">
              <Ticket className="w-5 h-5 text-[#FF007A]" />
              <span>Starting at <strong className="text-lg">$215</strong>/person</span>
            </div>
            <div className="flex items-center gap-2 font-outfit text-sm font-medium">
              <Clock className="w-5 h-5 text-[#00E5FF]" />
              <span>Full Day Experience</span>
            </div>
            <div className="flex items-center gap-2 font-outfit text-sm font-medium">
              <Phone className="w-5 h-5 text-[#9D00FF]" />
              <span>Instant Confirmation</span>
            </div>
          </div>

          <div id="v2h-booking" ref={widgetRef} className="min-h-[100px]" data-testid="booking-widget" />

          <div className="text-center mt-6 pt-6 border-t-2 border-gray-100">
            <p className="font-outfit text-sm text-[#4A4A4A] mb-4">
              Prefer to book by phone? Our 24/7 reservation team is standing by.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="tel:+18777418390" className="btn-primary px-6 py-3 text-sm" data-testid="booking-call-tollfree">
                <Phone className="w-4 h-4" />
                1-877-741-8390 (TOLL-FREE)
              </a>
              <a href="tel:+17029415144" className="btn-secondary px-6 py-3 text-sm" data-testid="booking-call-intl">
                <Phone className="w-4 h-4" />
                +1 (702) 941-5144
              </a>
            </div>
          </div>
        </motion.div>

        <div className="urgency-pulse text-center">
          <div className="inline-flex items-center gap-2 bg-[#FF007A]/10 border border-[#FF007A]/30 rounded-full px-4 py-2">
            <div className="w-2 h-2 rounded-full bg-[#FF007A] animate-ping" />
            <span className="font-outfit text-sm font-medium text-white">
              Limited availability — Tours fill up fast during peak season!
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
